/**
 * Messages d'un entretien structuré : ordre, échanges par question,
 * statistiques de transcription.
 */

import type { CheatingMeta } from "./cheating";
import type {
  EntStructCriteriaEtat,
  EntStructExchange,
  EntStructQuestionEtat,
} from "./scores";

export interface EntStructTranscriptionMeta {
  caracteresTranscrits: number;
  caracteresTotal: number;
  pourcentage: number;
}

export interface EntStructMessage {
  role: "assistant" | "user";
  content: string;
  questionId?: string;
  criteriaEtat?: EntStructCriteriaEtat[];
  // Qualité de la réponse évaluée par le chatbot (messages candidat uniquement,
  // absente sur les entretiens antérieurs à cette clé).
  responseQuality?: string;
  transcriptionMeta?: EntStructTranscriptionMeta;
  cheatingMeta?: CheatingMeta;
}

export interface TranscriptionStats {
  totalCaracteres: number;
  totalTranscrits: number;
  pourcentageGlobal: number;
  pourcentageMoyen: number;
  messagesVocaux: number;
  messagesTexte: number;
  messagesMixtes: number;
}

/**
 * Libellé de la qualité d'une réponse candidat (`response_quality` de l'API).
 * Valeurs connues : "off_topic" | "partial" | "good". `good` ne produit aucun
 * badge (cas nominal), une valeur inconnue est affichée brute plutôt que
 * masquée — l'énumération est pilotée côté chatbot.
 */
export function libelleQualiteReponse(
  quality?: string | null,
): { label: string; ton: "alerte" | "attention" | "neutre" } | null {
  if (!quality || quality === "good") return null;
  if (quality === "off_topic") return { label: "Hors sujet", ton: "alerte" };
  if (quality === "partial") return { label: "Partielle", ton: "attention" };
  return { label: quality, ton: "neutre" };
}

export function getMessagesFromReponses(
  reponses: Record<string, EntStructMessage>,
): EntStructMessage[] {
  return Object.entries(reponses)
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
    .map(([_, message]) => message);
}

/**
 * Reconstruit les échanges par question depuis la transcription, pour les
 * évaluations analysées avant que l'API ne renvoie `questions[].exchanges`.
 *
 * Un message assistant porte le `questionId` courant ; les messages candidat
 * qui le suivent y sont rattachés. `excluded_from_evaluation` reste absent (rien
 * ne permet de le déduire) et `response_quality` n'est présent que si le
 * message l'a stocké.
 */
export function construireExchangesDepuisReponses(
  reponses: Record<string, EntStructMessage>,
): Map<string, EntStructExchange[]> {
  const map = new Map<string, EntStructExchange[]>();
  let interviewerText = "";
  let questionId: string | undefined;

  for (const message of getMessagesFromReponses(reponses)) {
    const content = message.content.replace(/ENT_FINISHED/g, "").trim();
    if (message.role === "assistant") {
      interviewerText = content;
      questionId = message.questionId;
      continue;
    }
    if (!questionId) continue;
    const exchanges = map.get(questionId) ?? [];
    exchanges.push({
      interviewer_text: interviewerText,
      candidate_text: content,
      response_quality: message.responseQuality ?? null,
    });
    map.set(questionId, exchanges);
  }

  return map;
}

/**
 * Échanges à afficher par questionId. Les `exchanges` renvoyés par l'API sont
 * prioritaires (seuls à porter `excluded_from_evaluation`) ; à défaut on retombe
 * sur la transcription pour rester lisible sur l'historique.
 */
export function construireExchangesMap(
  questionsEtat: EntStructQuestionEtat[],
  reponses?: Record<string, EntStructMessage>,
): Map<string, EntStructExchange[]> {
  const derives = reponses
    ? construireExchangesDepuisReponses(reponses)
    : new Map<string, EntStructExchange[]>();

  const map = new Map<string, EntStructExchange[]>();
  for (const question of questionsEtat) {
    const exchanges = question.exchanges?.length
      ? question.exchanges
      : derives.get(question.questionId);
    if (exchanges?.length) map.set(question.questionId, exchanges);
  }
  return map;
}

export function calculerMoyenneCaracteres(
  reponses: Record<string, EntStructMessage>,
): { moyenne: number; nombreReponses: number } {
  const messages = getMessagesFromReponses(reponses);
  const reponsesUtilisateur = messages.filter((m) => m.role === "user");

  if (reponsesUtilisateur.length === 0) {
    return { moyenne: 0, nombreReponses: 0 };
  }

  const totalCaracteres = reponsesUtilisateur.reduce(
    (sum, m) => sum + m.content.replace(/ENT_FINISHED/g, "").trim().length,
    0,
  );

  return {
    moyenne: Math.round(totalCaracteres / reponsesUtilisateur.length),
    nombreReponses: reponsesUtilisateur.length,
  };
}

export function calculerStatsTranscription(
  reponses: Record<string, EntStructMessage>,
): TranscriptionStats {
  const messages = getMessagesFromReponses(reponses);
  const messagesUser = messages.filter((m) => m.role === "user");

  let totalCaracteres = 0;
  let totalTranscrits = 0;
  let sommePourcentages = 0;
  let messagesAvecMeta = 0;
  let messagesVocaux = 0;
  let messagesTexte = 0;
  let messagesMixtes = 0;

  for (const msg of messagesUser) {
    const length = msg.content.replace(/ENT_FINISHED/g, "").trim().length;
    totalCaracteres += length;

    if (msg.transcriptionMeta) {
      totalTranscrits += msg.transcriptionMeta.caracteresTranscrits;
      sommePourcentages += msg.transcriptionMeta.pourcentage;
      messagesAvecMeta++;

      if (msg.transcriptionMeta.pourcentage >= 80) {
        messagesVocaux++;
      } else if (msg.transcriptionMeta.pourcentage <= 20) {
        messagesTexte++;
      } else {
        messagesMixtes++;
      }
    } else {
      messagesTexte++;
    }
  }

  const pourcentageGlobal =
    totalCaracteres > 0
      ? Math.round((totalTranscrits / totalCaracteres) * 100)
      : 0;

  const pourcentageMoyen =
    messagesAvecMeta > 0 ? Math.round(sommePourcentages / messagesAvecMeta) : 0;

  return {
    totalCaracteres,
    totalTranscrits,
    pourcentageGlobal,
    pourcentageMoyen,
    messagesVocaux,
    messagesTexte,
    messagesMixtes,
  };
}
